/**
 * AuthService - React-agnostic authentication service
 *
 * Provides authentication state management using cookies and localStorage:
 * - Tracks login state via cookie inspection
 * - Stores user profile in localStorage
 * - Automatic token renewal before expiry
 * - Event-driven state changes
 */

import { logger } from '@/config/logging';
import { VERSION } from '@/config/version';
import { previewCache } from '@/features/docs/docs.preview-cache';
import { signOutWipe } from '@/features/offline/swBridge';
import { getClientIdentifier } from '@/lib/clientIdentifier';
import { setAuthStateDescriber } from '@/lib/authDiagnostics';
import { jwtDecode } from 'jwt-decode';
import { SessionUser } from '@certhon/models';

interface Session {
  user: SessionUser;
  accessToken: string;
  refreshToken: string;
}

// FIXME: use shared definitions
interface PasswordAuthCredentials {
  type: 'password';
  loginname: string;
  password: string;
}

interface TagAuthCredentials {
  type: 'tag';
  tagId: string;
  pin: string;
  clientId: string;
}
type AuthCredentials = PasswordAuthCredentials | TagAuthCredentials;

interface AuthServiceConfig {
  minValidityThreshold: number; // ms before expiry to refresh tokens
  checkInterval: number; // ms between token expiry checks
  autoRenew: boolean; // enable automatic token renewal
}

const DEFAULT_CONFIG: AuthServiceConfig = {
  minValidityThreshold: 60e3, // 1 minute
  checkInterval: 10 * 60e3, // 10 minutes
  autoRenew: true,
};

const STORAGE_KEY = 'auth_service_state';
const REFRESH_TOKEN_REFRESH_MARGIN = 5 * 24 * 3600e3; // 5 days

/**
 * Thrown by `patchCurrentUser` when `PATCH /me` answers with an error status,
 * so callers can tell a rejected value (e.g. 403 "Password too weak") from an
 * outage.
 */
/** Fields `PATCH /me` accepts: profile fields plus write-only credentials. */
export type CurrentUserPatch = Partial<
  Pick<SessionUser, 'name' | 'department' | 'settings' | 'language'>
> & {
  password?: string;
  pin?: string;
};

/** A rejected auth request (4xx) with the server's `{ error }` message. */
export class AuthRequestError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'AuthRequestError';
  }
}

export interface ActivateAccountInput {
  /** token from the invitation / reactivation mail */
  token: string;
  loginname: string;
  password: string;
  language: string;
}

export class ProfileUpdateError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: string,
  ) {
    super(`Failed to save profile: ${status}`);
    this.name = 'ProfileUpdateError';
  }
}

/**
 * Extract expiration time from JWT token
 */
function getJwtExpiration(jwt: string): number | null {
  try {
    const decoded = jwtDecode(jwt);
    return decoded.exp ? decoded.exp * 1000 : null; // Convert to milliseconds
  } catch (error) {
    tokenLogger.warn('Failed to decode JWT token', {
      error: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
}

/**
 * Get time until JWT expires (in milliseconds)
 */
function getJwtTTL(jwt: string): number | null {
  const expirationTime = getJwtExpiration(jwt);
  return expirationTime ? expirationTime - Date.now() : null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).getJwtTTL = getJwtTTL;

// [DEV] Expose auth helper for console use
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).__setDevAuth = (accessToken: string, refreshToken: string) => {
  // Lazy import to avoid circular dependency issues
  return AuthService.getInstance().setDevSession(accessToken, refreshToken);
};

// Loggers for different categories
const serviceLogger = logger.getChild(['service', 'auth']);
const tokenLogger = serviceLogger.getChild('token');
const storageLogger = serviceLogger.getChild('storage');
const apiLogger = serviceLogger.getChild('api');

// ---------------------------------------------------------------------------
// Re-auth diagnostics (#3576 "must log in again after closing Chrome")
//
// Every path that ends in the login screen records *why*, with the state of
// the browser at that moment. The record survives in localStorage, is logged
// again on the next load and is sent along with the next sign-in so the auth
// service log shows the reason per re-login across all users.
// ---------------------------------------------------------------------------

/**
 * Why the service decided a sign-in is needed.
 * - `no-refresh-token`: session in localStorage has no refresh token
 * - `refresh-token-expired`: the stored refresh JWT is past its `exp`
 * - `refresh-token-unreadable`: stored refresh JWT could not be decoded
 * - `network`: fetch threw (offline, DNS, VPN, reset) — no HTTP response
 * - `server`: HTTP 5xx from the proxy or the auth service
 * - `auth`: HTTP 401/403, the server rejected the refresh cookie
 * - `rejected`: any other non-2xx status
 */
export type ReauthReason =
  | 'no-refresh-token'
  | 'refresh-token-expired'
  | 'refresh-token-unreadable'
  | 'network'
  | 'server'
  | 'auth'
  | 'rejected';

export interface ReauthRecord {
  reason: ReauthReason;
  /** which call failed */
  endpoint: '/api/token/access' | '/api/token/refresh' | 'local';
  /** what triggered the call */
  trigger: 'init' | 'scheduled' | 'interval' | 'manual';
  at: string;
  status?: number;
  /** first bytes of the error body, when there was a response */
  body?: string;
  /** `Error.name: message` when fetch threw */
  error?: string;
  env: ReturnType<typeof envSnapshot>;
}

const LAST_REAUTH_KEY = 'auth_last_reauth';

/** Browser state relevant to the cold-start / connectivity hypotheses. */
function envSnapshot() {
  const nav = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;
  return {
    onLine: navigator.onLine,
    visibility: document.visibilityState,
    hasFocus: document.hasFocus(),
    /** navigate | reload | back_forward | prerender */
    navigationType: nav?.type ?? null,
    /** ms since this document started loading */
    msSinceLoad: Math.round(performance.now()),
    href: window.location.href,
    version: VERSION,
    swControlled: !!navigator.serviceWorker?.controller,
  };
}

/** Decoded lifetime of a JWT for logging; never the token itself. */
function describeJwt(jwt: string | undefined | null) {
  if (!jwt) return { present: false as const };
  try {
    const decoded = jwtDecode(jwt);
    const exp = decoded.exp ? decoded.exp * 1000 : null;
    const iat = decoded.iat ? decoded.iat * 1000 : null;
    return {
      present: true as const,
      issuedAt: iat ? new Date(iat).toISOString() : null,
      expiresAt: exp ? new Date(exp).toISOString() : null,
      ageMin: iat ? Math.round((Date.now() - iat) / 60e3) : null,
      ttlMin: exp ? Math.round((exp - Date.now()) / 60e3) : null,
      expired: exp ? exp <= Date.now() : null,
    };
  } catch (error) {
    return {
      present: true as const,
      unreadable: error instanceof Error ? error.message : String(error),
    };
  }
}

function classifyStatus(status: number): ReauthReason {
  if (status === 401 || status === 403) return 'auth';
  if (status >= 500) return 'server';
  return 'rejected';
}

function readLastReauth(): ReauthRecord | null {
  try {
    const raw = localStorage.getItem(LAST_REAUTH_KEY);
    return raw ? (JSON.parse(raw) as ReauthRecord) : null;
  } catch {
    return null;
  }
}

/** Non-session localStorage keys, to tell "storage wiped" from "signed out". */
function storageHealth() {
  try {
    return {
      keys: localStorage.length,
      hasClientIdentifier: !!localStorage.getItem(
        'certhon_app_client_identifier',
      ),
      hasLastReauth: !!localStorage.getItem(LAST_REAUTH_KEY),
    };
  } catch (error) {
    return { error: error instanceof Error ? error.message : String(error) };
  }
}

export class AuthService {
  private static instance: AuthService;
  private session: Session | null = null;
  private listeners: Set<() => void> = new Set();
  private config: AuthServiceConfig;
  private checkIntervalId: number | null = null;
  private accessTokenTimeoutId: number | null = null;
  private refreshTokenTimeoutId: number | null = null;
  private needsReauth: boolean = false;
  /** Why the last forced re-login happened; cleared by a successful sign-in. */
  private lastReauth: ReauthRecord | null = null;
  // #3577 diagnostics: refresh timing vs. tab visibility
  private lastRefreshAt: string | null = null;
  private refreshInFlight = false;
  private hiddenSince: number | null =
    document.visibilityState === 'hidden' ? Date.now() : null;
  private lastHiddenForMs: number | null = null;
  private lastVisibleAt: string | null = null;

  /** Promise that resolves when initial auth check is complete */
  private readyPromise: Promise<void>;
  private readyResolve!: () => void;

  private constructor(config: Partial<AuthServiceConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };

    // Create ready promise - resolved when initial token check completes
    this.readyPromise = new Promise((resolve) => {
      this.readyResolve = resolve;
    });

    this.lastReauth = readLastReauth();
    serviceLogger.info('AuthService starting', {
      env: envSnapshot(),
      storage: storageHealth(),
      lastReauth: this.lastReauth,
    });

    setAuthStateDescriber(() => this.describeState());
    this.observeLifecycle();
    this.loadUserFromStorage();
    this.initializeAuth().then(() =>
      serviceLogger.info('AuthService initialized', {
        config: this.config,
        isAuthenticated: this.isAuthenticated(),
        needsReauth: this.needsReauth,
        msSinceLoad: Math.round(performance.now()),
      }),
    );
  }

  /**
   * Initialize auth state - check tokens and refresh if needed
   */
  private async initializeAuth(): Promise<void> {
    const accessToken = this.session?.accessToken;

    if (!accessToken) {
      // No session - immediately ready
      serviceLogger.info('No session in storage, auth ready (login needed)', {
        storage: storageHealth(),
      });
      this.readyResolve();
      return;
    }

    const timeUntilExpiry = getJwtTTL(accessToken);
    const needsRefresh =
      timeUntilExpiry === null ||
      timeUntilExpiry <= this.config.minValidityThreshold;

    serviceLogger.info('Init token check', {
      needsRefresh,
      access: describeJwt(accessToken),
      refresh: describeJwt(this.session?.refreshToken),
      env: envSnapshot(),
    });

    if (needsRefresh) {
      serviceLogger.debug('Access token needs refresh during init');
      await this.refreshAccessToken('init');
    } else {
      // The token refresh answers with the profile, so it doubles as a profile
      // refresh; without one the session still holds whatever localStorage had
      // — possibly hours old, and waiting for the next renewal to correct it
      // is too long. Not awaited: nothing blocks on it, the cached profile
      // renders meanwhile.
      void this.refreshProfile();
    }

    serviceLogger.debug('Enabling readyPromise');
    this.readyResolve();

    // Start the regular renewal cycle
    this.startTokenRenewal();
  }

  /**
   * Re-read the profile from `/me` and put it in the session.
   *
   * Only for the case above: everywhere else the profile arrives with the
   * access token. Failure is silent — the cached profile stays.
   */
  /**
   * Re-read the profile (`GET /me`) into the current session, e.g. after a
   * server-side change that did not go through `patchCurrentUser` (email
   * confirmation). Failures are logged, not thrown.
   */
  async refreshProfile(): Promise<void> {
    // GET /me only yields the user; there must be a session (tokens) to merge
    // it into. Recovering a session from a refresh cookie is refreshAccessToken().
    if (!this.session) return;
    try {
      const response = await fetch('/api/me', { credentials: 'include' });
      if (!response.ok) {
        throw new Error(`Failed to load profile: ${response.status}`);
      }
      this.setCurrentUser(await response.json());
      apiLogger.debug('Profile refreshed');
    } catch (error) {
      apiLogger.warn('Profile refresh failed', {
        error: error instanceof Error ? error.message : String(error),
        offline: !navigator.onLine,
      });
    }
  }

  /**
   * Wait for auth service to be ready (initial token check and refresh complete)
   */
  waitForReady(): Promise<void> {
    return this.readyPromise;
  }

  static getInstance(config?: Partial<AuthServiceConfig>): AuthService {
    if (!AuthService.instance) {
      serviceLogger.debug('Creating new AuthService instance');
      AuthService.instance = new AuthService(config);
    } else {
      serviceLogger.trace('Returning existing AuthService instance');
    }
    return AuthService.instance;
  }

  /**
   * Check if user is currently authenticated
   * NOTE: the the tokens may not be valid, which requires re-auth
   */
  isAuthenticated(): boolean {
    return !!this.session;
  }

  /**
   * Check if user needs to re-authenticate
   */
  needsReAuthentication(): boolean {
    serviceLogger.debug('Re-authentication check', {
      needsReauth: this.needsReauth,
    });
    return this.needsReauth;
  }

  /**
   * Why the user last ended up on the login screen (or `null`). Kept across
   * reloads until a sign-in succeeds; `serv.auth.getLastReauth()` in the
   * console.
   */
  getLastReauth(): ReauthRecord | null {
    return this.lastReauth;
  }

  /** Snapshot for log lines elsewhere (failed API responses, error panels). */
  private describeState() {
    return {
      hasSession: !!this.session,
      needsReauth: this.needsReauth,
      access: describeJwt(this.session?.accessToken),
      refresh: describeJwt(this.session?.refreshToken),
      lastRefreshAt: this.lastRefreshAt,
      refreshInFlight: this.refreshInFlight,
      lastHiddenForMs: this.lastHiddenForMs,
      lastVisibleAt: this.lastVisibleAt,
    };
  }

  /**
   * #3577 hypothesis: a backgrounded tab (phone especially) freezes timers,
   * the scheduled refresh never runs, and on return the first requests leave
   * with an expired access cookie before the overdue refresh catches up.
   * Logging only: what the tokens look like the moment the tab comes back,
   * and whether a renewal timer was still pending.
   */
  private observeLifecycle(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.hiddenSince = Date.now();
        serviceLogger.debug('Tab hidden', {
          access: describeJwt(this.session?.accessToken),
          accessTimerPending: this.accessTokenTimeoutId !== null,
        });
        return;
      }
      const hiddenForMs = this.hiddenSince ? Date.now() - this.hiddenSince : 0;
      this.hiddenSince = null;
      this.lastHiddenForMs = hiddenForMs;
      this.lastVisibleAt = new Date().toISOString();
      const access = describeJwt(this.session?.accessToken);
      const expiredWhileHidden = 'expired' in access && access.expired === true;
      serviceLogger[expiredWhileHidden ? 'warn' : 'info'](
        'Tab visible again after {hiddenForMin} min',
        {
          hiddenForMin: Math.round(hiddenForMs / 60e3),
          hiddenForMs,
          expiredWhileHidden,
          access,
          refresh: describeJwt(this.session?.refreshToken),
          accessTimerPending: this.accessTokenTimeoutId !== null,
          refreshInFlight: this.refreshInFlight,
          lastRefreshAt: this.lastRefreshAt,
          needsReauth: this.needsReauth,
          env: envSnapshot(),
        },
      );
    });
    // bfcache restore: the document resumes with whatever timers it had
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      serviceLogger.info('Page restored from bfcache', {
        access: describeJwt(this.session?.accessToken),
        accessTimerPending: this.accessTokenTimeoutId !== null,
        env: envSnapshot(),
      });
    });
    window.addEventListener('online', () => {
      serviceLogger.info('Browser online', {
        access: describeJwt(this.session?.accessToken),
        needsReauth: this.needsReauth,
      });
    });
    window.addEventListener('offline', () => {
      serviceLogger.info('Browser offline', {
        access: describeJwt(this.session?.accessToken),
      });
    });
  }

  /**
   * The one place that flips to "sign in again": records the reason with the
   * browser state at that moment, logs it, notifies listeners.
   */
  private flagReauth(
    details: Omit<ReauthRecord, 'at' | 'env'>,
    level: 'warn' | 'error' = 'error',
  ): void {
    const record: ReauthRecord = {
      ...details,
      at: new Date().toISOString(),
      env: envSnapshot(),
    };
    this.lastReauth = record;
    try {
      localStorage.setItem(LAST_REAUTH_KEY, JSON.stringify(record));
    } catch {
      // storage unavailable — the log line below still has it
    }
    serviceLogger[level](
      'Re-authentication required: {reason} ({endpoint}, {trigger})',
      {
        ...record,
        access: describeJwt(this.session?.accessToken),
        refresh: describeJwt(this.session?.refreshToken),
      },
    );
    this.needsReauth = true;
    this.notifyListeners();
  }

  private clearLastReauth(): void {
    this.lastReauth = null;
    try {
      localStorage.removeItem(LAST_REAUTH_KEY);
    } catch {
      // nothing to clear
    }
  }

  /**
   * Get current user profile
   */
  getCurrentUser(): SessionUser | null {
    return this.session?.user || null;
  }

  /**
   * Sign in with credentials
   */
  async signIn({
    type,
    ...credentials
  }: AuthCredentials): Promise<SessionUser> {
    serviceLogger.info('Sign in attempt started', {
      type,
      loginname: 'loginname' in credentials ? credentials.loginname : undefined,
      tagId: 'tagId' in credentials ? credentials.tagId : undefined,
      password: 'password' in credentials ? '[redacted]' : undefined,
      pin: 'pin' in credentials ? '[redacted]' : undefined,
      // #3576: what put the user on the login screen, if anything did
      lastReauth: this.lastReauth,
      hadSession: !!this.session,
      previousUserId: this.session?.user.recnum,
      env: envSnapshot(),
    });

    // Before the request only: a stale cookie from a previous session must not
    // survive a failed login. The response below sets the fresh cookies.
    this.clearTokens();
    try {
      const response = await fetch('/api/token/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...credentials,
          clientId: getClientIdentifier(),
          clientVersion: VERSION,
          // #3576: the auth service logs this, so re-login reasons can be
          // read per user from the server log. Env without the URL.
          reauth: this.lastReauth
            ? {
                reason: this.lastReauth.reason,
                endpoint: this.lastReauth.endpoint,
                trigger: this.lastReauth.trigger,
                at: this.lastReauth.at,
                status: this.lastReauth.status,
                error: this.lastReauth.error,
                onLine: this.lastReauth.env.onLine,
                navigationType: this.lastReauth.env.navigationType,
                msSinceLoad: this.lastReauth.env.msSinceLoad,
              }
            : undefined,
        }),
      });

      apiLogger.debug('Sign in API response', {
        status: response.status,
        ok: response.ok,
        type,
      });

      if (!response.ok) {
        let errorMessage = `Authentication failed: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch {
          // Response body not JSON, use generic message
        }
        serviceLogger.error('Sign in failed', {
          status: response.status,
          type,
          error: errorMessage,
        });
        throw new Error(errorMessage);
      }

      const session: Session = await response.json();
      serviceLogger.info('Sign in successful', {
        userId: session.user.recnum,
        username: session.user.loginname,
        type,
        internal: session.user.internal,
      });

      this.setSession(session);
      this.needsReauth = false;
      this.clearLastReauth();
      this.startTokenRenewal();
      return session.user;
    } catch (error) {
      serviceLogger.error('Sign in error', {
        error: error instanceof Error ? error.message : String(error),
        type,
        env: envSnapshot(),
      });
      throw error;
    }
  }

  /**
   * Sets credentials on an invited (or reactivated) account and starts the
   * session the auth service issues with that answer. `POST /activate`
   * responds like `POST /token/refresh` (same payload, same cookies), so no
   * sign-in follows. Throws `AuthRequestError` for 4xx answers: username
   * taken, password too weak, token invalid or expired.
   */
  async activate(input: ActivateAccountInput): Promise<SessionUser> {
    serviceLogger.info('Account activation started', {
      loginname: input.loginname,
    });

    // Before the request only, as in signIn: a stale cookie from a previous
    // session must not survive a failed activation. The response sets the
    // fresh cookies.
    this.clearTokens();
    const response = await fetch('/api/activate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...input,
        clientId: getClientIdentifier(),
        clientVersion: VERSION,
      }),
    });
    if (!response.ok) {
      let message = `Activation failed: ${response.status}`;
      try {
        const data = await response.json();
        if (typeof data?.error === 'string') message = data.error;
      } catch {
        // not JSON, keep the generic message
      }
      serviceLogger.warn('Account activation rejected', {
        status: response.status,
        error: message,
      });
      throw new AuthRequestError(response.status, message);
    }
    const session: Session = await response.json();
    serviceLogger.info('Account activated', {
      userId: session.user.recnum,
      username: session.user.loginname,
    });
    // the server set the session cookies; only adopt the session here
    this.setSession(session);
    this.needsReauth = false;
    this.startTokenRenewal();
    return session.user;
  }

  /**
   * Sign out current user
   */
  signOut(): void {
    serviceLogger.info('Sign out initiated', {
      userId: this.session?.user.recnum,
      username: this.session?.user.loginname,
      // #3576: who called — a programmatic sign-out would also explain a
      // login screen. Top frames only.
      caller: new Error().stack?.split('\n').slice(2, 6).join(' | '),
      env: envSnapshot(),
    });

    this.clearTokens();
    this.needsReauth = false;
    this.stopTokenRenewal();
    this.clearSession();

    // Drop cached doc previews so signed-in content doesn't leak across
    // sessions on shared devices.
    previewCache.clear().catch((err) => {
      serviceLogger.warn('previewCache.clear failed on signOut', { err });
    });

    // Wipe all SW offline data (entity caches, upload/event queues,
    // attachments) — same shared-device rationale. Callers should warn the
    // user first when the sync status reports pending offline changes.
    signOutWipe().catch((err) => {
      serviceLogger.warn('SW offline wipe failed on signOut', { err });
    });

    serviceLogger.info('Sign out completed');
  }

  /**
   * [DEV ONLY] Set session directly from tokens without API calls.
   * Useful for testing authentication states from the browser console.
   *
   * Usage: window.__setDevAuth(accessToken, refreshToken)
   */
  setDevSession(accessToken: string, refreshToken: string): SessionUser {
    serviceLogger.warn('[DEV] Setting session directly from tokens');

    // Decode access token to build user info
    const accessPayload = jwtDecode<{
      type: string;
      recnum: number;
      username: string;
      perm: number;
      internal: boolean;
    }>(accessToken);

    // Build minimal SessionUser from token payload
    const user: SessionUser = {
      recnum: accessPayload.recnum,
      name: accessPayload.username,
      loginname: accessPayload.username,
      internal: accessPayload.internal,
      active: true,
      email: `${accessPayload.username}@dev.local`,
      email_new: null,
      language: 'en',
      permissions: accessPayload.perm,
      department: null,
      settings: {},
      tag_id: null,
      requested_tag_id: null,
      requested_tag_on: null,
      expires_on: null,
    };

    // Set cookies for API requests
    const accessExp = getJwtExpiration(accessToken);
    const refreshExp = getJwtExpiration(refreshToken);
    if (accessExp) {
      document.cookie = `access=${accessToken}; path=/api; expires=${new Date(accessExp).toUTCString()}`;
    }
    if (refreshExp) {
      document.cookie = `refresh=${refreshToken}; path=/api/token; expires=${new Date(refreshExp).toUTCString()}`;
    }

    const session: Session = { user, accessToken, refreshToken };
    this.setSession(session);
    this.needsReauth = false;
    this.startTokenRenewal();

    serviceLogger.info('[DEV] Session set successfully', {
      userId: user.recnum,
      username: user.loginname,
      internal: user.internal,
      permissions: user.permissions,
    });

    return user;
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshAccessToken(
    trigger: ReauthRecord['trigger'] = 'manual',
  ): Promise<void> {
    tokenLogger.info('Access token refresh started ({trigger})', {
      trigger,
      access: describeJwt(this.session?.accessToken),
      refresh: describeJwt(this.session?.refreshToken),
      env: envSnapshot(),
    });

    const localReason = this.localRefreshTokenProblem();
    if (localReason) {
      this.flagReauth(
        { reason: localReason, endpoint: 'local', trigger },
        'warn',
      );
      return;
    }

    const startedAt = performance.now();
    this.refreshInFlight = true;
    let response: Response;
    try {
      response = await fetch('/api/token/access', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          clientId: getClientIdentifier(),
          clientVersion: VERSION,
        }),
      });
    } catch (error) {
      this.refreshInFlight = false;
      // No HTTP response at all: offline, DNS, VPN not up, connection reset,
      // or the request aborted by navigation.
      this.flagReauth({
        reason: 'network',
        endpoint: '/api/token/access',
        trigger,
        error:
          error instanceof Error
            ? `${error.name}: ${error.message}`
            : String(error),
      });
      return;
    }
    this.refreshInFlight = false;
    const durationMs = Math.round(performance.now() - startedAt);

    apiLogger.debug('Access token refresh API response ({status})', {
      status: response.status,
      ok: response.ok,
      durationMs,
      degraded: response.headers.get('x-sw-degraded'),
    });

    if (!response.ok) {
      const body = await response.text().catch(() => '');
      this.flagReauth({
        reason: classifyStatus(response.status),
        endpoint: '/api/token/access',
        trigger,
        status: response.status,
        body: body.slice(0, 300),
      });
      return;
    }

    try {
      const userData = await response.json();
      tokenLogger.info('Access token refresh successful', {
        trigger,
        durationMs,
        userId: userData.user?.recnum ?? userData.recnum,
        username: userData.user?.loginname ?? userData.loginname,
        access: describeJwt(userData.accessToken),
      });

      this.lastRefreshAt = new Date().toISOString();
      this.setSession({ ...this.session, ...userData });
      this.needsReauth = false;
      this.scheduleTokenChecks();
    } catch (error) {
      // 2xx with an unreadable body — proxy error page or similar
      this.flagReauth({
        reason: 'rejected',
        endpoint: '/api/token/access',
        trigger,
        status: response.status,
        error:
          error instanceof Error
            ? `${error.name}: ${error.message}`
            : String(error),
      });
    }
  }

  /**
   * Replace the profile inside the current session, keeping the tokens.
   *
   * Everything that reads `getCurrentUser()` — including the localStorage copy
   * restored on the next load — sees the new value immediately.
   *
   * There is no separate "reload the profile" call: `POST /token/access`
   * answers with the full profile, so `refreshAccessToken` already replaces it
   * on every renewal. A profile edited elsewhere lands at the next one.
   */
  private setCurrentUser(user: SessionUser): void {
    if (!this.session) return;
    this.setSession({ ...this.session, user });
  }

  /**
   * Persist a profile change through `PATCH /me`.
   *
   * Applied to the session first so the UI reacts instantly, and rolled back
   * when the request fails — which is what happens offline, so callers should
   * keep editing controls disabled while there is no connection.
   *
   * Write-only fields (`password`, `pin`) are sent but never applied to the
   * session.
   */
  async patchCurrentUser(patch: CurrentUserPatch): Promise<SessionUser> {
    if (!this.session) throw new Error('Not signed in');

    const previous = this.session.user;
    const profilePatch: Partial<SessionUser> = { ...patch };
    delete (profilePatch as CurrentUserPatch).password;
    delete (profilePatch as CurrentUserPatch).pin;
    this.setCurrentUser({ ...previous, ...profilePatch });

    try {
      const response = await fetch('/api/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(patch),
      });
      if (!response.ok) {
        throw new ProfileUpdateError(response.status, await response.text());
      }
      const user: SessionUser = await response.json();
      this.setCurrentUser(user);
      return user;
    } catch (error) {
      this.setCurrentUser(previous);
      apiLogger.error('Profile update failed', {
        error: error instanceof Error ? error.message : String(error),
        offline: !navigator.onLine,
      });
      throw error;
    }
  }

  /**
   * Asks for the email address to be changed. The server keeps `email` as is,
   * stores the new address as `email_new` and mails a confirmation link
   * (/confirm-email); only then does `email` change. Nothing is applied
   * optimistically, the returned user carries the pending address.
   */
  async requestEmailChange(email: string): Promise<SessionUser> {
    if (!this.session) throw new Error('Not signed in');
    const response = await fetch('/api/me', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email }),
    });
    if (!response.ok) {
      throw new ProfileUpdateError(response.status, await response.text());
    }
    const user: SessionUser = await response.json();
    this.setCurrentUser(user);
    return user;
  }

  /**
   * Subscribe to auth state changes
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    serviceLogger.debug('Auth state listener added', {
      totalListeners: this.listeners.size,
    });
    return () => {
      this.listeners.delete(listener);
      serviceLogger.debug('Auth state listener removed', {
        totalListeners: this.listeners.size,
      });
    };
  }

  /**
   * Load user data from localStorage
   */
  private loadUserFromStorage(): void {
    try {
      const storedUser = localStorage.getItem(STORAGE_KEY);
      if (storedUser) {
        this.session = JSON.parse(storedUser);
        storageLogger.info('User loaded from localStorage', {
          userId: this.session?.user.recnum,
          username: this.session?.user.loginname,
          access: describeJwt(this.session?.accessToken),
          refresh: describeJwt(this.session?.refreshToken),
        });
      } else {
        // #3576: with the client identifier also gone the whole origin's
        // storage was wiped (browser setting / policy), not just our session.
        storageLogger.warn('No session in localStorage', {
          storage: storageHealth(),
        });
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      storageLogger.error('Failed to load user from localStorage', {
        error: errorMessage,
      });
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  /**
   * Save session data to localStorage
   */
  private saveSessionToStorage(session: Session): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
      storageLogger.debug('Session saved to localStorage', {
        userId: session.user.recnum,
        username: session.user.loginname,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      storageLogger.error('Failed to save session to localStorage', {
        error: errorMessage,
        userId: session.user.recnum,
        username: session.user.loginname,
      });
    }
  }

  /**
   * Set current session and notify listeners
   */
  private setSession(session: Session): void {
    const previousSession = this.session;
    this.session = session;
    this.saveSessionToStorage(session);

    serviceLogger.info('Session updated', {
      userId: session.user.recnum,
      username: session.user.loginname,
      previousUserId: previousSession?.user.recnum,
      isNewUser:
        !previousSession || previousSession.user.recnum !== session.user.recnum,
    });

    this.notifyListeners();
  }

  /**
   * Clear current user
   */
  private clearSession(): void {
    const previousUser = this.session;
    this.session = null;
    localStorage.removeItem(STORAGE_KEY);

    serviceLogger.info('User session cleared', {
      previousUserId: previousUser?.user.recnum,
      previousUsername: previousUser?.user.loginname,
    });

    this.notifyListeners();
  }

  /**
   * Clear auth tokens from cookies
   */
  private clearTokens(): void {
    tokenLogger.debug('Clearing auth tokens from cookies');

    // Clear access token cookies
    document.cookie =
      'access=; path=/api; expires=Thu, 01 Jan 1970 00:00:00 GMT';

    // Clear refresh token cookies
    document.cookie =
      'refresh=; path=/api/token; expires=Thu, 01 Jan 1970 00:00:00 GMT';

    tokenLogger.info('Auth tokens cleared from cookies');
  }

  /**
   * Local (pre-request) check of the stored refresh token. The cookie itself
   * cannot be inspected from the page (path `/api/token`), so this is the only
   * client-side signal; the server decides on the cookie.
   *
   * Returns the reason it cannot be used, or `null` when it looks fine.
   */
  private localRefreshTokenProblem(): ReauthReason | null {
    const refreshToken = this.session?.refreshToken;
    if (!refreshToken) {
      tokenLogger.warn('No refresh token in stored session', {
        sessionKeys: this.session ? Object.keys(this.session) : null,
      });
      return 'no-refresh-token';
    }

    const ttl = getJwtTTL(refreshToken);
    if (ttl === null) {
      tokenLogger.warn('Could not parse refresh token expiration');
      return 'refresh-token-unreadable';
    }

    const isValid = ttl > 0;
    tokenLogger.debug('Refresh token validity check', {
      isValid,
      refresh: describeJwt(refreshToken),
    });
    return isValid ? null : 'refresh-token-expired';
  }

  /**
   * Notify all listeners of state changes
   */
  private notifyListeners(): void {
    serviceLogger.debug('Notifying auth state listeners', {
      listenerCount: this.listeners.size,
      isAuthenticated: this.isAuthenticated(),
      needsReauth: this.needsReauth,
    });
    this.listeners.forEach((listener) => listener());
  }

  /**
   * Start automatic token renewal
   */
  private startTokenRenewal(): void {
    if (!this.config.autoRenew) {
      tokenLogger.debug('Auto-renewal disabled, skipping token renewal setup');
      return;
    }

    tokenLogger.info('Starting automatic token renewal', {
      checkInterval: this.config.checkInterval,
      minValidityThreshold: this.config.minValidityThreshold,
    });

    this.stopTokenRenewal();
    this.scheduleTokenChecks('scheduled');
    this.checkIntervalId = window.setInterval(() => {
      this.scheduleTokenChecks('interval');
    }, this.config.checkInterval);
  }

  /**
   * Stop automatic token renewal
   */
  private stopTokenRenewal(): void {
    const hadActiveTimers = !!(
      this.checkIntervalId ||
      this.accessTokenTimeoutId ||
      this.refreshTokenTimeoutId
    );

    if (this.checkIntervalId) {
      clearInterval(this.checkIntervalId);
      this.checkIntervalId = null;
    }
    if (this.accessTokenTimeoutId) {
      clearTimeout(this.accessTokenTimeoutId);
      this.accessTokenTimeoutId = null;
    }
    if (this.refreshTokenTimeoutId) {
      clearTimeout(this.refreshTokenTimeoutId);
      this.refreshTokenTimeoutId = null;
    }

    if (hadActiveTimers) {
      tokenLogger.info('Automatic token renewal stopped');
    }
  }

  /**
   * Schedule token renewal checks
   */
  private scheduleTokenChecks(
    trigger: ReauthRecord['trigger'] = 'scheduled',
  ): void {
    this.scheduleRefreshTokenRenewal(trigger);
    this.scheduleAccessTokenRenewal(trigger);
  }

  /**
   * Schedule access token renewal
   */
  private scheduleAccessTokenRenewal(trigger: ReauthRecord['trigger']): void {
    const accessToken = this.session?.accessToken;
    if (!accessToken) {
      tokenLogger.debug('No access token found for renewal scheduling');
      return;
    }

    const timeUntilExpiry = getJwtTTL(accessToken);
    if (timeUntilExpiry === null) {
      tokenLogger.debug('Could not parse access token for renewal scheduling');
      return;
    }

    const timeUntilRenewal = timeUntilExpiry - this.config.minValidityThreshold;

    if (timeUntilRenewal > 0) {
      tokenLogger.debug(
        'Access token renewal scheduled in {timeUntilRenewal}s',
        {
          timeUntilExpiry: timeUntilExpiry / 1e3,
          timeUntilRenewal: timeUntilRenewal / 1e3,
        },
      );
      if (this.accessTokenTimeoutId) clearTimeout(this.accessTokenTimeoutId);
      this.accessTokenTimeoutId = window.setTimeout(() => {
        this.accessTokenTimeoutId = null;
        this.refreshAccessToken('scheduled');
      }, timeUntilRenewal);
    } else {
      if (timeUntilExpiry > 0) {
        tokenLogger.debug('Access token expires soon, refreshing now', {
          timeUntilExpiry,
        });
      } else {
        tokenLogger.warn('Access token already expired, trying to refresh', {
          timeUntilExpiry,
          trigger,
        });
      }
      this.refreshAccessToken(trigger);
    }
  }

  /**
   * Schedule refresh token renewal
   */
  private scheduleRefreshTokenRenewal(trigger: ReauthRecord['trigger']): void {
    const refreshToken = this.session?.refreshToken;
    if (!refreshToken) {
      tokenLogger.debug('No refresh token found for renewal scheduling');
      return;
    }

    const timeUntilExpiry = getJwtTTL(refreshToken);
    if (timeUntilExpiry === null) {
      tokenLogger.debug('Could not parse refresh token for renewal scheduling');
      return;
    }

    const timeUntilRenewal = timeUntilExpiry - REFRESH_TOKEN_REFRESH_MARGIN;

    if (timeUntilRenewal > 0) {
      tokenLogger.debug('Refresh token renewal scheduled', {
        timeUntilExpiry,
        timeUntilRenewal,
        refreshMargin: REFRESH_TOKEN_REFRESH_MARGIN,
      });
      if (this.refreshTokenTimeoutId) clearTimeout(this.refreshTokenTimeoutId);
      this.refreshTokenTimeoutId = window.setTimeout(() => {
        this.refreshTokenTimeoutId = null;
        this.refreshRefreshToken('scheduled');
      }, timeUntilRenewal);
    } else if (timeUntilExpiry > 0) {
      tokenLogger.info('Refresh token expires soon, refreshing immediately', {
        timeUntilExpiry,
        trigger,
      });
      this.refreshRefreshToken(trigger);
    } else {
      // Past expiry: the next access-token refresh reports
      // `refresh-token-expired` (hypothesis "not used for 7 days").
      tokenLogger.warn('Refresh token already expired', {
        refresh: describeJwt(refreshToken),
        trigger,
      });
    }
  }

  /**
   * Refresh the refresh token
   */
  private async refreshRefreshToken(
    trigger: ReauthRecord['trigger'] = 'manual',
  ): Promise<void> {
    if (!this.session) {
      tokenLogger.debug('No user available for refresh token renewal');
      return;
    }

    tokenLogger.info('Refresh token renewal started ({trigger})', {
      trigger,
      refresh: describeJwt(this.session.refreshToken),
      env: envSnapshot(),
    });

    const startedAt = performance.now();
    let response: Response;
    try {
      response = await fetch('/api/token/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          clientId: getClientIdentifier(),
          clientVersion: VERSION,
        }),
      });
    } catch (error) {
      this.flagReauth({
        reason: 'network',
        endpoint: '/api/token/refresh',
        trigger,
        error:
          error instanceof Error
            ? `${error.name}: ${error.message}`
            : String(error),
      });
      return;
    }
    const durationMs = Math.round(performance.now() - startedAt);

    apiLogger.debug('Refresh token renewal API response ({status})', {
      status: response.status,
      ok: response.ok,
      durationMs,
      degraded: response.headers.get('x-sw-degraded'),
    });

    if (!response.ok) {
      const body = await response.text().catch(() => '');
      this.flagReauth({
        reason: classifyStatus(response.status),
        endpoint: '/api/token/refresh',
        trigger,
        status: response.status,
        body: body.slice(0, 300),
      });
      return;
    }

    try {
      const userData = await response.json();
      tokenLogger.info('Refresh token renewal successful', {
        trigger,
        durationMs,
        userId: userData.user?.recnum ?? userData.recnum,
        username: userData.user?.loginname ?? userData.loginname,
        refresh: describeJwt(userData.refreshToken),
        access: describeJwt(userData.accessToken),
      });

      this.setSession({ ...this.session, ...userData });
      this.needsReauth = false;
      this.scheduleTokenChecks();
    } catch (error) {
      this.flagReauth({
        reason: 'rejected',
        endpoint: '/api/token/refresh',
        trigger,
        status: response.status,
        error:
          error instanceof Error
            ? `${error.name}: ${error.message}`
            : String(error),
      });
    }
  }
}

export default AuthService;
